import React, { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DateTimePicker } from '@material-ui/pickers';

import {
  aggregatedSurveyResponsesForSurveyIdSelector,
  EmployeeSurveyResponse,
} from './survey-response-selector';
import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { userSelector } from '../../reducers/current-user-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import {
  surveyForIdSelector,
  surveyResponsesStateForSurveyIdSelector,
} from '../../reducers/survey-selectors';
import { loadSurveyResponses } from '../actions/survey/load-survey-responses';
import { loadSurveys } from '../actions/survey/load-surveys';
import { updateSurvey } from '../actions/survey/update-survey';

import * as Api from '../../api/api';
import { FeatureFlagged, FeatureGatedVariant } from '../../feature-gates/feature-flagged';
import { FeatureGates } from '../../feature-gates/feature-gates';
import { MessageItem } from '../../messages/components/message-item';
import { TextMessage } from '../../messages/legacy-components/text-message';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';
import { UiSortLabel } from '../../ui/ui-sort-label';
import { EmployeeProfileDialog } from '../profile/employee-profile-dialog';

import { AddResponseDialog } from './add-response-dialog';
import { CreateSurveyDialog } from './create-survey-dialog';
import { EmployeeSurveyDetailFilter } from './employee-survey-detail-filter';
import { EmployeeSurveyDetailRow } from './employee-survey-detail-row';
import { SendReminderDialog } from './send-reminder-dialog';
import { SurveyResponseProgressBar } from './survey-response-progress-bar';

import { surveyExporter } from './utils/survey-exporter';
import { surveyRecipients } from './utils/survey-recipients';
import { formatDateTime } from '../../util/formatters';
import { HTMLStripper } from '../../util/html-stripper';
import { LanguageCode } from '../../util/languages';
import { MESSAGE_TYPE } from '../../util/message-types';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { error } from '../../widgets/error-message-dialog';
import { LoadingMessage } from '../../widgets/loading-message';
import { collectMessageToLanguage } from '../../widgets/message-composer/wrappers/multiple-message-composer-dialog';

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  org: orgSelector(state, ownProps.orgId),
  survey: surveyForIdSelector(state, ownProps.surveyId),
  surveyResponses: aggregatedSurveyResponsesForSurveyIdSelector(state, ownProps.surveyId),
  surveyResponsesState: surveyResponsesStateForSurveyIdSelector(state, ownProps.surveyId),
  user: userSelector(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadSurveyResponses: () => dispatch(loadSurveyResponses(ownProps.surveyId)),
    loadSurveys: () => dispatch(loadSurveys(ownProps.orgId)),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(ownProps.orgId)),
    updateSurvey: (update: UpdateSurveyRequest) =>
      dispatch(updateSurvey(ownProps.surveyId, update)),
  },
});

type InputProps = {
  orgId: number;
  surveyId: number;
};
type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const cleanOptionalString = (answer: string | null | undefined) =>
  HTMLStripper.strip(answer || '') || '';

const statusThenNameComparator = (a: EmployeeSurveyResponse, b: EmployeeSurveyResponse) => {
  // Answers - if they both have them, use name to compare.
  // Otherwise, bump up the one who has an answer
  if (a.hasAnswer && b.hasAnswer) {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  }
  if (a.hasAnswer) {
    return -1;
  }
  if (b.hasAnswer) {
    return 1;
  }

  // Errors - again, if they both errored, then compare on name.
  // Otherwise, bump up the one who didn't error
  if (a.hasError && b.hasError) {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  }
  if (a.hasError) {
    return 1;
  }
  if (b.hasError) {
    return -1;
  }

  // Lastly, these must both not have a response yet. Then sort by
  // name
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
};

const compareSurveyResponsesBy = (
  field: string,
  a: EmployeeSurveyResponse,
  b: EmployeeSurveyResponse,
) => {
  switch (field) {
    case 'status':
      return statusThenNameComparator(a, b);
    case 'name':
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    case 'answeredAt':
      return (b.firstResponseTime?.valueOf() ?? 0) - (a.firstResponseTime?.valueOf() ?? 0);
    case 'firstResponse':
      if (a.hasAnswer && b.hasAnswer) {
        const [aFirstResponse] = a.responses;
        const [bFirstResponse] = b.responses;

        const aAnswer = cleanOptionalString(aFirstResponse.answer);
        const bAnswer = cleanOptionalString(bFirstResponse.answer);

        return aAnswer.toLowerCase().localeCompare(bAnswer.toLowerCase());
      }
      if (a.hasAnswer) {
        return -1;
      }
      if (b.hasAnswer) {
        return 1;
      }
      return 0;
    default:
      return statusThenNameComparator(a, b);
  }
};

export const EmployeeSurveyDetail = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    actions,
    survey,
    surveyId,
    surveyResponses,
    org,
    orgId,
    user,
    surveyResponsesState,
  }: Props) => {
    useEffect(() => {
      actions.loadSurveys();
      actions.loadSurveyResponses();
      actions.loadOrgEmployeeGroups();
    });

    const theme = useTheme();
    const onMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [responsesToShow, setResponsesToShow] = useState<string>('ALL');
    const [isSendingReminder, setIsSendingReminder] = useState<boolean>(false);
    const [isAddingEmployee, setIsAddingEmployee] = useState<boolean>(false);
    const [isReopeningSurvey, setIsReopeningSurvey] = useState<boolean>(false);
    const [endAt, setEndAt] = useState<Moment | null>(moment().add(2, 'days'));
    const [editSurveyResponseId, setEditSurveyResponseId] = useState<number | null>(null);
    const [sortBy, setSortBy] = useState<string>('answeredAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [isEditingMessage, setIsEditingMessage] = useState<boolean>(false);
    const [employeeIdProfileToShow, setEmployeeIdProfileToShow] = useState<number | null>(null);
    const [displayIrrelevant, setDisplayIrrelevant] = useState<boolean>(false);

    if (!survey || !surveyResponses || !org || !surveyResponsesState) {
      return null;
    }

    const isSurveyRunning = moment().isBefore(moment(survey.endAt));

    const sortedEmployeeResponses = surveyResponses
      .sort((a: EmployeeSurveyResponse, b: EmployeeSurveyResponse) => {
        const result = compareSurveyResponsesBy(sortBy, a, b);
        return sortDirection === 'desc' ? result : -result;
      })
      .filter((resp: EmployeeSurveyResponse) => {
        switch (responsesToShow) {
          case 'ALL':
            return true;
          case 'RESPONDED':
            return resp.hasAnswer;
          case 'NOT_RESPONDED':
            return !resp.hasAnswer && !resp.hasError;
          case 'FAILED':
            return !resp.hasAnswer && resp.hasError;
          default:
            return true;
        }
      });

    const handleSendReminderClick = () => setIsSendingReminder(!isSendingReminder);
    const handleAddEmployeeClick = () => setIsAddingEmployee(!isAddingEmployee);
    const handleEditMessageClick = () => setIsEditingMessage(!isEditingMessage);
    const handleAddEmployeeClose = () => setIsAddingEmployee(false);
    const handleEmployeeNameClick = (employeeId: number) => () =>
      setEmployeeIdProfileToShow(employeeId);
    const handleEditClick = (surveyResponseId?: number) => () =>
      setEditSurveyResponseId(surveyResponseId || null);
    const handleEditClose = () => setEditSurveyResponseId(null);
    const handleFailedClick = () => setResponsesToShow('FAILED');
    const handleReopenSurveyClick = () => setIsReopeningSurvey(!isReopeningSurvey);
    const handleCloseEmployeeProfile = () => setEmployeeIdProfileToShow(null);
    const saveNewSurveyEndDate = () => {
      actions.updateSurvey({ endAt });
      setIsReopeningSurvey(false);
    };
    const downloadFile = async () => {
      const res = await Api.get(`/surveys/survey-send-times?surveyId=${surveyId}`);
      const surveyTimes = (await res.json()) as { [employeeId: number]: string };
      const result = surveyExporter(surveyResponses, user, survey, surveyTimes);
      if (!result) {
        await error();
      }
    };

    const handleEditReminderMessage = async () => {
      const languageToMessage: Record<LanguageCode, string> = {};
      if (survey.reminderMessageBody) {
        languageToMessage.en = survey.reminderMessageBody;
      }
      if (survey.reminderSpanishMessageBody) {
        languageToMessage.es = survey.reminderSpanishMessageBody;
      }
      const languagesToSupport = ['en', 'es'];

      const returnVal = await collectMessageToLanguage(
        'Edit Reminder Message',
        'Edit the reminder message that will be sent one day before the survey ends',
        {
          enableText: true,
          enableEmail: false,
          languageToMessage,
          languagesToSupport,
          buttonClass: 'u-float-right',
          buttonText: 'Save Reminder Message',
        },
      );

      if (returnVal) {
        actions.updateSurvey({
          reminderMessageBody: returnVal.languageToMessage.en,
          reminderSpanishMessageBody: returnVal.languageToMessage.es,
        });
      }
    };

    const hasAllEmployeesPermission = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;

    return (
      <>
        <div
          className={`u-display-flex u-alignItems-center u-width-100 u-justifyContent-spaceBetween ${
            onMobile ? 'u-flexDirection-column' : ''
          }`}
        >
          <div className="u-width-100" data-cy="survey-message-container">
            <div className="typo-subtitle">
              To: {surveyRecipients(survey)} {survey.isAnonymous ? '(anonymous survey)' : ''} <br />
            </div>
            <div className={onMobile ? 'u-width-100' : 'u-width-40'}>
              {survey.messageType === MESSAGE_TYPE.TEXT && (
                <>
                  <FeatureFlagged
                    variant={FeatureGatedVariant.HIDE_ON_FLAG}
                    flagName={FeatureGates.INTEGRATED_MESSAGING}
                  >
                    <TextMessage
                      senderName={org.name}
                      sendDateTime={survey.sentAt as unknown as string}
                      messageBody={survey.messageBody}
                      isInbound={false}
                      isAutomated
                      isFailed={false}
                      otherLanguages={
                        survey.spanishMessageBody
                          ? [
                              {
                                languageCode: 'es',
                                messageBody: survey.spanishMessageBody,
                              },
                            ]
                          : undefined
                      }
                      forceAllowEditing={surveyResponses.length === 0}
                      onEditMessage={
                        surveyResponses.length === 0 ? handleEditMessageClick : undefined
                      }
                      dataCy="survey-text"
                    />
                  </FeatureFlagged>
                  <FeatureFlagged
                    variant={FeatureGatedVariant.SHOW_ON_FLAG}
                    flagName={FeatureGates.INTEGRATED_MESSAGING}
                  >
                    <MessageItem
                      messageType={MESSAGE_TYPE.TEXT}
                      senderName={org.name}
                      sendDateTime={survey.sentAt as unknown as string}
                      messageBody={survey.messageBody}
                      isInbound={false}
                      isFailed={false}
                      otherLanguages={
                        survey.spanishMessageBody
                          ? [
                              {
                                languageCode: 'es',
                                messageBody: survey.spanishMessageBody,
                              },
                            ]
                          : undefined
                      }
                      forceAllowEditing={surveyResponses.length === 0}
                      onEditMessage={
                        surveyResponses.length === 0 ? handleEditMessageClick : undefined
                      }
                      dataCy="survey-text"
                      isPreview
                    />
                  </FeatureFlagged>
                </>
              )}
              {survey.sendAutomatedReminders && (
                <div className="u-spread u-width-100">
                  <div className="typo-subtitle">
                    {survey.reminderSentAt
                      ? `Reminder was sent at ${formatDateTime(
                          moment.utc(survey.reminderSentAt).toDate(),
                        )}`
                      : `Reminder will be sent at ${formatDateTime(
                          moment.utc(survey.endAt).subtract(1, 'day').toDate(),
                        )}`}
                  </div>
                  {!survey.reminderSentAt && (
                    <div
                      role="presentation"
                      onClick={handleEditReminderMessage}
                      className="typo-subtitle typo-link u-margin-left-md"
                    >
                      Edit Reminder Message
                    </div>
                  )}
                </div>
              )}
              <SurveyResponseProgressBar surveyId={surveyId} onClickFailed={handleFailedClick} />
            </div>
          </div>
        </div>
        {hasAllEmployeesPermission && !isSurveyRunning && (
          <UiButton
            className="u-margin-top-md u-margin-right-sm"
            variant="tertiary"
            onClick={handleReopenSurveyClick}
          >
            Reopen Survey
          </UiButton>
        )}
        {hasAllEmployeesPermission && (
          <UiButton
            className="u-margin-top-md u-margin-right-sm"
            variant="tertiary"
            onClick={handleAddEmployeeClick}
          >
            Add Employees
          </UiButton>
        )}
        {(isAddingEmployee || isEditingMessage) && (
          <CreateSurveyDialog
            orgId={orgId}
            surveyId={surveyId}
            onClose={isAddingEmployee ? handleAddEmployeeClose : handleEditMessageClick}
            surveyType={survey.surveyType}
            isAddingEmployee={isAddingEmployee}
          />
        )}
        {isSurveyRunning && hasAllEmployeesPermission && (
          <UiButton
            className="u-margin-top-md u-margin-right-sm"
            variant="tertiary"
            onClick={handleSendReminderClick}
          >
            Send Reminders
          </UiButton>
        )}
        <UiButton className="u-margin-top-md" variant="tertiary" onClick={downloadFile}>
          Download Responses
        </UiButton>
        {isSendingReminder && (
          <SendReminderDialog
            orgId={orgId}
            surveyId={surveyId}
            surveyType={survey.surveyType}
            onClose={handleSendReminderClick}
          />
        )}
        {isReopeningSurvey && (
          <UiDialog size="xs" onClose={handleReopenSurveyClick} title="Reopen Survey">
            <UiDialog.Content>
              <div>Please select a new end date</div>
              <DateTimePicker
                className="u-width-100 u-margin-bottom-md"
                label="End Survey On"
                value={endAt}
                onChange={setEndAt}
                disablePast
              />
            </UiDialog.Content>
            <UiDialog.Actions>
              <UiButton variant="dismiss" onClick={handleReopenSurveyClick}>
                Cancel
              </UiButton>
              <UiButton variant="primary" onClick={saveNewSurveyEndDate}>
                Reopen
              </UiButton>
            </UiDialog.Actions>
          </UiDialog>
        )}
        <EmployeeSurveyDetailFilter
          responsesToShow={responsesToShow}
          displayIrrelevant={displayIrrelevant}
          setDisplayIrrelevant={setDisplayIrrelevant}
          setResponsesToShow={setResponsesToShow}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <UiSortLabel
                  label="Status"
                  sortKey="status"
                  currentSortBy={sortBy}
                  currentSortDirection={sortDirection}
                  onSortByChange={setSortBy}
                  onSortDirectionChange={setSortDirection}
                />
              </TableCell>
              {!survey.isAnonymous && (
                <TableCell>
                  <UiSortLabel
                    label="Employee"
                    sortKey="name"
                    currentSortBy={sortBy}
                    currentSortDirection={sortDirection}
                    onSortByChange={setSortBy}
                    onSortDirectionChange={setSortDirection}
                  />
                </TableCell>
              )}
              <TableCell>
                <UiSortLabel
                  label="Response"
                  sortKey="firstResponse"
                  currentSortBy={sortBy}
                  currentSortDirection={sortDirection}
                  onSortByChange={setSortBy}
                  onSortDirectionChange={setSortDirection}
                />
              </TableCell>
              <TableCell>
                <UiSortLabel
                  label="Received"
                  sortKey="answeredAt"
                  currentSortBy={sortBy}
                  currentSortDirection={sortDirection}
                  onSortByChange={setSortBy}
                  onSortDirectionChange={setSortDirection}
                />
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {surveyResponsesState.loading && sortedEmployeeResponses.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <LoadingMessage />
                </TableCell>
              </TableRow>
            )}
            {sortedEmployeeResponses.map((employeeResponse) => (
              <EmployeeSurveyDetailRow
                key={employeeResponse.employeeId}
                orgId={orgId}
                employeeResponse={employeeResponse}
                displayIrrelevant={displayIrrelevant}
                handleEmployeeNameClick={handleEmployeeNameClick}
                handleEditClick={handleEditClick}
                cleanOptionalString={cleanOptionalString}
                isAnonymousSurvey={survey.isAnonymous}
              />
            ))}
            {editSurveyResponseId && (
              <AddResponseDialog
                editSurveyResponseId={editSurveyResponseId}
                onClose={handleEditClose}
              />
            )}
          </TableBody>
        </Table>
        {employeeIdProfileToShow && (
          <EmployeeProfileDialog
            orgId={orgId}
            employeeId={employeeIdProfileToShow}
            fullScreen={false}
            onClose={handleCloseEmployeeProfile}
          />
        )}
      </>
    );
  },
);
